import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {HideableValue, PartiallyOptional} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    asArray,
    autoValueIsAutoValue,
    hideableValueCreateHidden,
    hideableValueGetValue,
    hideableValueIsVisible,
    hideableValueIsVisibleAndNotEqualValue,
    immerProduce
} from '@wix/devzai-utils-common';
import type {DoppeActionTypeVideo} from '../doppe-action-type-video/doppe-action-type-video';
import {DoppeActionTypeVideoRatio} from '../doppe-action-type-video/doppe-action-type-video';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction,
    DoppeActionLayout,
    doppeBymoPageVideoSourceResolveVideoUrl,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemSettingsMigrateBaseSettings,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage
} from '../../client-server-common';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';


export namespace DoppeActionTypeVideosList {

    export interface VideoSettings extends
        DoppeActionTypeVideosList.DeprecatedItemFields,
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeActionTypeVideo.VideoSettings {
        title: string;
    }

    export interface ActionSettings extends
        DoppeListActionSettings<VideoSettings>,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings
    {
        previewShowTitle: boolean;
        previewShowDescription: boolean;
    }

    export type ActionDeprecatedProps = Partial<DoppeListActionSettings.WithCtaButton> & {
        videos?: VideoSettings[];
        previewButtonText?: string;
    };

    export interface DeprecatedItemFields extends Partial<DoppeActionCtaButtonSettings> {
        videoButtonText?: HideableValue<string>;
        videoButtonURL?: DoppeActionTypeVideo.DoppeActionTypeVideoURLSource;
    }

    export type PartialAndDeprecatedActionSettings = DoppeListActionSettings.ResolvePartialAndDeprecatedActionSettings<
        ActionSettings,
        DoppeActionTypeVideosList.VideoSettings,
        'listDataItems'
    > & ActionDeprecatedProps
}

export const doppeActionTypeVideosList = doppeActionTypeDefineMetadata<
    DoppeActionTypeVideosList.ActionSettings,
    DoppeActionTypeVideosList.PartialAndDeprecatedActionSettings
>({
    id: 'io.bymo.action/videos-list',
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            ctaButton,
            previewShowDescription = true,
            previewShowTitle = true,
            layout = DoppeActionLayout.Grid,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            previewShowDescription: previewShowDescription,
            previewShowTitle: previewShowTitle,
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings({
                ...action.settings,
                layout: layout,
                listDataItems: listDataItems.map(video => {

                    const {
                        title = 'Video Title',
                        videoButtonText,
                        videoButtonURL,
                        ctaButton,
                        customSections,
                        customCtaButtons,
                        sectionsOverridesMap,
                        ctaButtonsOverridesMap,
                        videoProvider = null,
                        videoProviderId = null,
                        videoRatio = DoppeActionTypeVideoRatio.Auto,
                        ...restVideoSettings
                    } = video;

                    return {
                        videoProvider: videoProvider,
                        videoProviderId: videoProviderId,
                        videoButtonURL: videoButtonURL,
                        videoButtonText: videoButtonText,
                        title: title,
                        ...getDefaultDoppeActionCtaButtonSettings({
                            buttonText: videoButtonText ? hideableValueGetValue(videoButtonText) : '',
                            showButton: hideableValueIsVisible(videoButtonText),
                            url: videoButtonURL ? (autoValueIsAutoValue(videoButtonURL) ? undefined : videoButtonURL) : undefined
                        }),
                        ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                            hasSourceURL: true,
                            ctaButton: ctaButton,
                        }),
                        ...doppeListActionListItemSettingsMigrateBaseSettings(video),
                        ...doppeListActionListItemWithCtaButtonsMigration(video, true),
                        ...doppeListActionListItemWithSectionsMigration(video),
                        ...restVideoSettings as PartiallyOptional<DoppeActionTypeVideosList.VideoSettings & DoppeActionTypeVideosList.DeprecatedItemFields, 'title' | 'videoProviderId' | 'videoProvider' | 'ctaButton' | 'videoButtonURL' | 'videoButtonText' | 'customCtaButtons' |'customSections' | 'ctaButtonsOverridesMap' | 'sectionsOverridesMap'>,
                        videoRatio: videoRatio,
                    }
                }),
            }),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({
                defaultTitle: ''
            }, action.settings),
            ...convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(action.settings),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                ctaButton: ctaButton,
                hasSourceURL: false
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeVideosList.ActionSettings
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails()
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            }
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            videoImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                description: true,
                videoButtonText: true
            }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(actionSettings.listDataItems.map(article => {
            return [article.videoImage]
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, actionSettings.listDataItems[0]?.videoImage);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveListItemsCount: actionSettings => {
        return doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: action => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            return {
                title: item.title,
                action: action,
                couponCode: hideableValueCreateHidden(''),
                description: item.description,
                image: item.videoImage ? asArray(item.videoImage)[0] : null,
                itemId: item.id,
                ribbon: hideableValueCreateHidden(''),
                url: doppeBymoPageVideoSourceResolveVideoUrl(item.externalVideoUrl),
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.videoImage) {
                    item.videoImage =
                        wixMediaResourceReplaceExternalImage(item.videoImage, externalImagesUrlToImageResourceMap)
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);

        })
    },
});